import React, { useMemo } from 'react';
import useUrlState from 'Hooks/useUrlState';
import PastPaypalPaymentsTable from './Table/PastPaypalPaymentsTable';
import useAbortableEffect from 'Hooks/useAbortableEffect';
import styles from '../PastPayments.module.css';
import useMicrowavePayments from '../../useMicrowavePayments';
import PastPaymentsFilters, { getFilterValues } from '../PastPaymentsFilters/PastPaymentsFilters';
import { microwave } from '@round/api';
import Header from 'ui-new/Layout/Header/Header';

type UrlState = microwave.GetPaymentRequestsParams;

const initialUrlState: Pick<Required<UrlState>, 'page' | 'page_size' | 'payment_status'> = {
    page: 1,
    page_size: 10,
    payment_status: 'PAID',
};

const PastPaypalPayments = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>(initialUrlState);

    const page = urlState.page ? Number(urlState.page) : initialUrlState.page;
    const pageSize = urlState.page_size ? Number(urlState.page_size) : initialUrlState.page_size;

    const filters = useMemo(() => getFilterValues(urlState), [urlState]);
    const apiFilters = useMemo(() => ({ ...filters, microwave_influencer_account_id: undefined }), [filters]);

    const { rows, count, status, fetchData, reset } = useMicrowavePayments();

    useAbortableEffect(
        (signal) => {
            if (status === 'not-initialized') {
                fetchData(
                    {
                        page,
                        page_size: pageSize,
                        preferred_payment_method: 'PAYPAL',
                        status: 'APPROVED,APPROVED_HIDDEN',
                        ...apiFilters,
                    },
                    { signal }
                );
            }
        },
        [status, fetchData, page, pageSize, apiFilters]
    );

    const isInitialized = status === 'initialized' || status === 'error';

    return (
        <div className={styles.container}>
            <Header className={styles.header}>
                <Header.Title>Past Paypal payments</Header.Title>

                <div className={styles.optionsContainer}>
                    <PastPaymentsFilters
                        value={filters}
                        onChange={(val) => {
                            setUrlState({ ...val, page: 1 });
                            reset();
                        }}
                    />
                </div>
            </Header>

            <PastPaypalPaymentsTable
                isLoading={!isInitialized}
                data={rows}
                count={count}
                page={page}
                setPage={(page) => {
                    setUrlState({ page });
                    reset();
                }}
                pageSize={pageSize}
                setPageSize={(pageSize) => {
                    setUrlState({ page_size: pageSize || initialUrlState.page_size });
                    reset();
                }}
                manualPagination
                noDataLabel={status === 'error' ? 'Could not load payments' : 'No payments found'}
            />
        </div>
    );
};

export default PastPaypalPayments;
